import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Libraries,
	StandaloneSearchBox,
	useJsApiLoader,
} from '@react-google-maps/api';
import { Modal } from '../../../globalComponents/modal/Modal';
import { ActionButtons } from '../../../globalComponents/modal/ActionButtons';
import { useApi } from '../../../hooks/useApi';
import {
	useGlobalDispatch,
	useGlobalState,
} from '../../../stateProviders/GlobalStateProvider';
import { QuotationCreateParams } from '../../../types/api/Quotation';
import React from 'react';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import { Stack, Typography } from '@mui/material';
import getDefaultDeckbox from '../views/blocks/utils/getDefaultDeckbox';
import { toastError } from '../../../Toaster/Toast';

interface AddQuotationModalProps {
	modalOpen: boolean;
	setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const mapsLibraries: Libraries = ['places'];

const AddQuotationModal: React.FC<AddQuotationModalProps> = ({
	modalOpen,
	setModalOpen,
}) => {
	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY || '',
		libraries: mapsLibraries,
	});

	const [quotation, setQuotation] = useState<QuotationCreateParams>({
		projectName: '',
		customerName: '',
		zipCode: '',
		address: '',
		blocks: [],
	});
	const api = useApi();
	const globalDispatch = useGlobalDispatch();
	const globalStae = useGlobalState();
	const [existingQuotation, setExistingQuotation] = useState(false);
	const navigate = useNavigate();
	const addressRef = useRef<google.maps.places.SearchBox>();

	const handleOnPlacesChanged = () => {
		if (addressRef.current) {
			const places = addressRef.current.getPlaces();
			const place = places?.[0];

			if (place) {
				const formattedAddress = place.formatted_address || '';
				const hasStreetNumber = place.address_components?.some((component) =>
					component.types.includes('street_number')
				);
				const hasRoute = place.address_components?.some((component) =>
					component.types.includes('route')
				);

				if (!hasStreetNumber || !hasRoute) {
					toastError({
						title: 'Incomplete Address',
						description:
							'Please enter a complete street address (e.g., 123 Main St, City, State)',
					});
					setQuotation((prev) => ({
						...prev,
						address: '',
						zipCode: '',
					}));
					return;
				}

				const zipComponent = place.address_components?.find((component) =>
					component.types.includes('postal_code')
				);
				const zipCode = zipComponent?.long_name || '';
				console.log('The zip code is', zipCode);
				if (!zipCode) {
					toastError({
						title: 'Invalid Address',
						description: 'Please select an address with a valid zip code',
					});
					setQuotation((prev) => ({
						...prev,
						address: formattedAddress,
						zipCode: '',
					}));
				} else {
					setQuotation((prev) => ({
						...prev,
						address: formattedAddress,
						zipCode: zipCode,
					}));
				}
			}
		}
	};

	return (
		<Modal
			open={modalOpen}
			onClose={() => setModalOpen(false)}
			modalTitle={
				<Typography variant="h2" fontWeight="bold">
					Add Quote
				</Typography>
			}
			modalBody={
				<Stack spacing={2}>
					{[
						{
							label: 'Project Name',
							name: 'projectName',
							placeholder: 'Example: Johnson Residence Outdoor Deck',
							value: quotation.projectName,
						},
						{
							label: 'Customer Name',
							name: 'customerName',
							placeholder: 'Example: Johnson Freeman',
							value: quotation.customerName,
						},
						{
							label: 'Address',
							name: 'address',
							placeholder: 'Example: 1234 Oak Lane, Beverly Hills, CA',
							value: quotation.address,
						},
					].map((field) => (
						<Stack key={field.name}>
							<FormLabel component="legend" sx={{ mb: 1 }} required>
								{field.label}
							</FormLabel>

							{field.name == 'address' && isLoaded && (
								<StandaloneSearchBox
									onLoad={(ref) => (addressRef.current = ref)}
									onPlacesChanged={handleOnPlacesChanged}
								>
									<TextField
										fullWidth
										name={field.name}
										placeholder={field.placeholder}
										value={field.value}
										onChange={(e) =>
											setQuotation({
												...quotation,
												[field.name]: e.target.value,
											})
										}
									/>
								</StandaloneSearchBox>
							)}

							{field.name != 'address' && (
								<TextField
									fullWidth
									name={field.name}
									placeholder={field.placeholder}
									value={field.value}
									onChange={(e) =>
										setQuotation({ ...quotation, [field.name]: e.target.value })
									}
								/>
							)}
						</Stack>
					))}
				</Stack>
			}
			modalFooter={
				<ActionButtons
					confirmButtonText="Save"
					confirmCallback={async () => {
						setExistingQuotation(false);
						const createdQuote = await api.quotations.createQuotation({
							...quotation,
							blocks: [
								{
									...getDefaultDeckbox(),
									label: 'DeckBox A',
									bumpouts: [],
									landings: [],
									stairs: [],
									ref: null,
									position: { x: 100, y: 100 },
								},
							],
						});

						const quotations = await api.quotations.getQuotations();
						globalDispatch({ quotations: quotations.rows });
						navigate('/quotations/' + createdQuote._id);
					}}
					confirmDisabled={
						!Object.values(quotation).every((q) =>
							Array.isArray(q) ? true : q.trim() !== ''
						) || existingQuotation
					}
					confirmButtonStyle="success"
					cancelCallback={() => {
						setModalOpen(false);
						setExistingQuotation(false);
					}}
				/>
			}
		/>
	);
};

export default AddQuotationModal;
